import { digitalSignature } from '@/services/axios'
import { $mx_hasValue } from '@/mixins/util'
import { $mx_generateSignerKeyword, isJSON } from '@/mixins/helpers'

import Vue from 'vue'

const lodash = require("lodash")

import {
    onlineDefaultSigners,
    docuDefaultSigners,
    clickDefaultSigners
} from '@/assets/js/data'

const typeLabels = {
    customer: 'Cliente do Negócio',
    user: 'Usuário Facilita',
    manager: 'Gerente de Vendas',
    broker: 'Corretor de Imóveis',
    nonuser: 'Usuário Não Facilita'
}

/**
 * Recebe os dados dos campos customizados de um proponente e retorna um
 * objeto JSON com as informações necessárias para torná-lo um assinante
 *
 * @param {Object} fields Campos customizados do proponente
 * @returns {Object} JSON contendo um objeto gerado a partir dos campos
 */
const proponentCredentials = (fields) => {
    if (!fields || fields.length == 0) return null

    let name = null
    let email = null
    let birthday = null
    let cpf = null
    let whatsapp_number = null

    /**
     * Busca nos campos, aqueles que guardam as configurações de nome, email,
     * data de nascimento e CPF/CNPJ do proponente
     */
    for (const field of fields) {
        // Verifica se o campo possui a configuração de signature_bidder
        const bidderKey = Object.keys(field.options || [])
            .find(key => key.includes('signature_bidder'))

        /**
         * Caso possua, verifica se é de uma das configurações necessárias para atuar
         * como assinante
         */
        if (bidderKey) {
            switch (field.options[bidderKey]) {
                case 'name':
                    name = field.value?.value || null
                    break

                case 'email':
                    email = field.value?.value || null
                    break

                case 'mobile':
                    whatsapp_number = field.value?.value || null
                    break

                case 'birthday':
                    birthday = field.value?.value || null
                    break

                case 'cpf':
                    cpf = field.value?.value || null
                    break
            }
        }
    }

    return {
        name,
        email,
        birthday,
        cpf,
        whatsapp_number,
        can_sign: !!(name && email),
        communicate_events: {
			signature_request: "email"
		},
        optional: false
    }
}

/**
 * Valida a propriedade required dos campos do formulário
 */
const validateRequired = (form, field, dataField = '') => {
    // if (!Object.prototype.hasOwnProperty.call(form, field)) {
    //     throw new Error(`Falha ao validar o campo ${field}. Este campo não existe!`)
    // }

    dataField = dataField || field
    const validation = lodash.get(form, dataField) ||
        parseInt(lodash.get(form, dataField)) === 0

    return validation
}

const validateUser = (signer, required) => {
    let valid = true
    let errors = {}
    for (const field of required) {
        if (!validateRequired(signer, field)) {
            valid = false
            switch (field) {
                case 'name':
                    errors[field] = 'Por favor informar o nome do assinante.'
                    break

                case 'email':
                    errors[field] = 'Por favor informar um e-mail para o assinante.'
                    break

                case 'signature_type':
                    errors[field] = 'Por favor informar o tipo de assinatura do assinante.'
                    break

                case 'keyword.name':
                    errors[field] = 'Por favor informar um marcador para o assinante.'
                    break

                case 'cpf':
                    errors[field] = 'Por favor informar o CPF do assinante.'
                    break

                case 'birthday':
                    errors[field] = 'Por favor informar a data de nascimento do assinante.'
                    break

                case 'role':
                    errors[field] = 'Por favor informar o papel do assinante.'
                    break

                case 'auth':
                    errors[field] = 'Por favor informar a prova de identidade do assinante.'
                    break
            }
        }
    }

    return { valid, errors }
}

const validateConfig = (config, required) => {
    let valid = true
    let errors = {}
    for (const field of required) {
        if (!validateRequired(config, field)) {
            valid = false
            switch (field) {
                case 'role':
                    errors[field] = 'Por favor informar o papel do assinante.'
                    break

                case 'signature_type':
                    errors[field] = 'Por favor informar o tipo de assinatura do assinante.'
                    break

                case 'keyword.name':
                    errors[field] = 'Por favor informar um marcador para o assinante.'
                    break

                case 'type':
                    errors[field] = 'Por favor informar tipo de assinante.'
                    break
            }
        }
    }

    return { valid, errors }
}

// TO-DO: Trocar essa função pela função criada na mixins
const toastFormatMessages = (response) => {
    if( !response?.data ) return

	const responseData = response.data
    let toastMessage = 'Ocorreu um erro inesperado. Tente novamente mais tarde.';

    // Adiciona o título (msg) como a primeira linha
    if (responseData?.msg) toastMessage = `${responseData.msg}<br>`;

    // Adiciona os detalhes, se existirem
    if (responseData?.details) {
        toastMessage = `<strong>${responseData.msg}:</strong><br>`;

        const details = Object.values(responseData.details)
            .flat() // Junta os arrays internos
            .map(message => `- ${message}`) // Adiciona um marcador em cada mensagem
            .join('<br>'); // Junta todas as mensagens com <br>

        toastMessage += `${details}`;
    }

    Vue.prototype.$toastr.e(toastMessage)
    return 
}


export const digitalSignatureStore = {
    namespaced: true,

    state: {
        loading: false,
        product: {
            id: 0,
            name: '-'
        },
        integration: null,
        signers: [],
        envelope: null,
        deal: null,
        files: []
    },

    getters: {
        getLoading: state => state.loading,
        getProduct: state => state.product,
        getEnvelope: state => state.envelope,
        getIntegration: state => state.integration,
        getSigners: state => {
            let signers = new Map();
            state.signers.forEach((signer, index) => {
                signer._uid = `uid_${index}`;
                signers.set(signer._uid, signer);
            });
            let signersArray = Array.from(signers.values());
            return signersArray;
        },
        getDeal: state => state.deal,
        getFiles: state => state.files,
        getFormValidation(state) {
            let result = {
                valid: true,
                errors: {}
            }

            // Define os campos obrigatórios, conforme o tipo de integração
            let userRequired = ['name', 'email', 'role']
            let configRequired = ['role', 'type']
            switch (state.integration?.slug) {
                case 'assine_online':
                    userRequired.push('signature_type')
                    configRequired.push('signature_type')
                    break

                case 'docu_sign':
                    userRequired.push('keyword.name')
                    configRequired.push('keyword.name')
                    break

                case 'click_sign':
                    userRequired.push('auth')
                    configRequired.push('auth')
                    if (state.envelope?.has_documentation) {
                        userRequired = userRequired.concat(['cpf', 'birthday'])
                    }
            }

            const selected = Object.values(state.signers).filter(signer => signer.selected)
            for (const signer of selected) {
                let validation = { valid: true, errors: {} }

                // Valida usuários não Facilita ou Facilita
                if (signer.type === 'nonuser' || state.deal) {
                    validation = validateUser(signer, userRequired)

                // Valida configurações padrões
                } else {
                    validation = validateConfig(signer, configRequired)
                }

                if (!validation.valid) result.valid = validation.valid
                result.errors[signer.group] = validation.errors
            }

            return result
        }
    },

    mutations: {
        SET_LOADING(state, payload) {
            state.loading = payload
        },
        SET_PRODUCT(state, payload) {
            state.product = payload ? { ...payload } : null
            state.integration = null
            state.signers = []
            state.envelope = null
            state.deal = null
            state.files = []
        },
        SET_ENVELOPE(state, payload) {
            state.envelope = $mx_hasValue(Object.keys(payload)) ? {...payload } : null
        },
        SET_INTEGRATION(state, payload) {
            state.integration = payload ? { ...payload } : null
        },
        SET_SIGNERS(state, payload) {
            state.signers = payload ? [...payload] : []
        },
        REMOVE_INTEGRATION(state) {
            state.integration = null
            state.signers = []
            state.envelope = null
        },
        SET_FILES(state, payload) {
            state.files = payload && isJSON(payload) ? JSON.parse(payload) : []
        },
        SET_DEAL(state, payload) {
            state.deal = payload ? { ...payload } : null
        }
    },

    actions: {
        /**
         * Seta o estado do loading no state
         *
         * @param {Boolean} payload Estado do loading
         */
        setLoading({ commit }, payload) {
            commit('SET_LOADING', payload)
        },
        /**
         * Seta os dados do produto que está configurando a integração
         *
         * @param {Object} payload Dados do produto a ser configurado
         */
        setProduct({ commit }, payload) {
            commit('SET_PRODUCT', payload)
        },
        /**
         * Seta os dados da integração escolhida para ser configurada para o
         * empreendimento selecionado
         *
         * @param {*} payload Dados da integração
         */
        setIntegration({ commit }, payload) {
            commit('SET_INTEGRATION', payload)
        },
        /**
         * Salva as configurações necessárias para logar em uma integração
         *
         * @param {Object} payload Dados necessários para realizar a integração
         */
        integrate({ getters }, payload) {
            if (!getters.getIntegration?.id) {
                Vue.prototype.$toastr.e('Não existe nenhuma integração selecionada/configurada!')
                return
            }

            return digitalSignature.createV2(
                `integration/${getters.getIntegration?.id}`,
                { configurations: {...payload } }
            )
        },
        /**
         * Solicita que as configurações da integração selecionada sejam removidas
         */
        desintegrate({ commit, getters }) {
            if (!getters.getIntegration?.id) {
                Vue.prototype.$toastr.e('Não existe nenhuma integração selecionada/configurada!')
                return
            }

            return new Promise((resolve, reject) => {
                digitalSignature.deleteV2('integration', getters.getIntegration?.id)
                    .then(response => {
                        Vue.prototype.$toastr.s(response.data.msg)
                        commit('REMOVE_INTEGRATION')
                        resolve()
                    })
                    .catch(error => {
						toastFormatMessages(error)
                        reject()
                    })
            })
        },
        /**
         * Realiza uma solicitação de modo a verificar se a integração está funcionando
         */
        authenticate({ getters }) {
            const integration = { ...getters.getIntegration }
            return digitalSignature.createV2('authenticate', { integration_id: integration.id })
        },
        /**
         * Carrega as configurações de assinatura do empreendimento atualmente selecionado
         *
         * @returns
         */
        loadCurrentProductConfigs({ dispatch, getters }) {
            if (!(getters.getIntegration && getters.getProduct)) return

            dispatch('loadProductConfigs', {
                product_id: getters.getProduct?.id,
                integration_id: getters.getIntegration?.id,
                slug: getters.getIntegration?.slug
            })
        },
        /**
         * Carrega as configurações de assinatura de um empreendimento para determinada
         * integração
         */
        loadProductConfigs: async function ({ commit, getters }, payload) {
            if (!(payload?.integration_id && payload?.product_id)) return

            try {
                commit('SET_LOADING', true)

                let params = {
                    type:'product',
                    integration_id: payload.integration_id,
                    deal_id: getters.getDeal?.id
                }
                let response = await digitalSignature.getV2('signers', payload.product_id, params)
                //[FAS-3993] Adicionado um filter provisorio até criar uma nova regra pra comportar o fato de haver vários supevisores para uma equipe
                response.data.data = response.data.data.filter(signer => signer.type !== 'supervisor');
                /**
                 * Processa o array de opções de assinantes default para setar os dados dos assinantes
                 * cadastrados
                 */
                let signers = []
                switch (payload?.slug) {
                    case 'assine_online':
                        signers = [...onlineDefaultSigners()]
                        break

                    case 'docu_sign':
                        signers = [...docuDefaultSigners()]
                        break

                    case 'click_sign':
                        signers = [...clickDefaultSigners()]
                        break
                }
                for (let signer of Object.values(response.data.data)) {
                    signer.selected = true
                    /**
                     * Para os assinantes do DocuSign, envia-se um objeto com os marcadores
                     * a serem utilizados no documento
                     */
                    if (payload?.slug === 'docu_sign') {
                        const base = signer.type !== 'nonuser'
                            ? signer.type
                            : ''
                        if (!signer.keyword) {
                            signer.keyword = $mx_generateSignerKeyword(base, signer.role)

                        } else if (signer.keyword && isJSON(signer.keyword)) {
                            signer.keyword = JSON.parse(signer.keyword)
                        }

                        if(signer?.optional == null){
                            signer.optional = false;
                        }
                    }

                    if (payload?.slug === 'click_sign') {
                        if(!signer?.communicate_events?.signature_request){
                            signer.communicate_events = {};
                            signer.communicate_events.signature_request = "email"
                        }
                    }

                    // Não verifica usuários externos, pois podem existir vários
                    const index = !['nonuser', 'user'].includes(signer.type)
                        ? signers.findIndex(dSigner => dSigner.type === signer.type)
                        : -1
                    if (index >= 0) {
                        signers.splice(index, 1, signer)

                    } else {
                        signers.push(signer)
                    }
                }

				const orderedSigners = signers.sort((a, b) => a.group - b.group)
                commit('SET_SIGNERS', orderedSigners)

                if (['docu_sign', 'click_sign'].includes(payload?.slug)) {
                    params = { ...payload }
                    delete params.slug
                    response = await digitalSignature.getAllV2('configuration-envelope', params)
                    commit('SET_ENVELOPE', response.data)
                }

            } catch (error) {
				toastFormatMessages(error)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        /**
         * Seta os dados dos assinantes a serem configurados para a integração no state
         *
         * @param {Array} payload Dados dos assinantes a serem configurados para a integração
         */
        setSigners({ commit }, payload) {
            commit('SET_SIGNERS', payload)
        },
        /**
         * Recebe o indíce de um assinante cadastrado e o remove da lista de assinantes
         *
         * @param {String|Number} payload Indíce do assinante a ser removido
         */
        removeSigner({ commit, getters }, payload) {
            const index = parseInt(payload)
            let signers = [...getters.getSigners]
            if (signers[index]) {
                signers.splice(index, 1)

            } else {
                Vue.prototype.$toastr.w('O índice informado não pertence a um Assinante cadastrado!')
            }

            commit('SET_SIGNERS', signers)
        },
        /**
         * Seta os dados do envelope a serem configurados para a integração no state
         *
         * @param {Object} payload Dados do envelope a serem configurados
         */
        setEnvelope({ commit }, payload) {
            commit('SET_ENVELOPE', payload)
        },
        /**
         * Salva as configurações de assinantes
         */
        saveConfigs: async function ({ commit, getters }) {
            try {
                commit('SET_LOADING', true)
                let params = {
                    integration_id: getters.getIntegration?.id,
                    products_id: getters.getProduct?.id,
                    digitalSignatureSigners: Object.groupBy(
                        getters.getSigners
                            .filter(signer => signer.selected)
                            .map((signer, index) => {
                                signer.group = index + 1
                                /**
                                 * Para as configurações do ClickSign, seta a configuração has_documentation
                                 * do envelope para todos
                                 */
                                if (getters.getIntegration?.slug === 'click_sign') {
                                    signer.has_documentation = getters.getEnvelope?.has_documentation
                                }

                                return signer
                            }),
                        ({ type }) => type
                    )
                }

                let response = await digitalSignature.createV2('signers', params)
                Vue.prototype.$toastr.s(response.data.msg)

                if (['docu_sign', 'click_sign'].includes(getters.getIntegration?.slug)) {
                    params = {
                        integration_id: getters.getIntegration?.id,
                        product_id: getters.getProduct?.id,
                        ...getters.getEnvelope
                    }
                    response = await digitalSignature.createV2('configuration-envelope', params)
                    Vue.prototype.$toastr.s(response?.data?.msg)
                }

            } catch (error) {
				toastFormatMessages(error)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        /**
         * Recebe os dados de um negócio e os seta no state
         *
         * @param {Object} payload Dados do negócio para cadastrar o fluxo
         */
        setDeal({ commit }, payload) {
            commit('SET_DEAL', payload)
        },
        /**
         * Seta os IDs dos documentos selecionados para assinatura no state
         *
         * @param {Array} payload Array contendo os IDs dos documentos
         */
        setFiles({ commit, dispatch }, payload) {
            commit('SET_FILES', payload)
        },
        /**
         * Carrega as configurações de assinantes do empreendimento vinculado ao negócio,
         * e seta os dados dos proponentes e responsáveis de acordo com a configuração
         */
        loadDealSigners: async function ({ commit, getters, dispatch }) {
            if (!getters.getDeal?.id) {
                Vue.prototype.$toastr.e('Falha ao carregar as configurações! Nenhum negócio foi selecionado.')
                return
            }

            try {
                commit('SET_LOADING', true)
                let params = {
                    type: 'product',
                    integration_id: getters.getIntegration?.id,
                    deal_id: getters.getDeal?.id
                }
                let response = await digitalSignature.getV2('signers', getters.getProduct?.id, params)
                let signers = [...response.data.data]

                if (getters.getIntegration?.slug === 'click_sign') {
                    signers = signers.map(signer => {
                        if(!signer?.communicate_events?.signature_request){
                            signer.communicate_events = {};
                            signer.communicate_events.signature_request = "email"
                        }
                        return signer
                    })
                }

                signers = await dispatch('handleSigners', signers)

                /**
                 * Seta somente os assinantes selecionados para envio e reseta o sua posição de
                 * assinatura conforme a ordem
                 */
                commit(
                    'SET_SIGNERS',
                    signers.filter(signer => signer.selected)
                        .map((signer, index) => {
                            signer.group = index + 1

                            return signer
                        })
                )

            } catch (error) {
				toastFormatMessages(error)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        /**
         * Solicita o envio do envelope, conforme as configurações definidas no state
         */
        saveEnvelope: async function ({ commit, getters }) {
            return new Promise(async (resolve, reject) => {
                try {
                    commit('SET_LOADING', true)
                    let response = null
                    if (['docu_sign', 'click_sign'].includes(getters.getIntegration?.slug)) {
                        const params = {
                            integration_id: getters.getIntegration?.id,
                            deal_id: getters.getDeal?.id,
                            file_id: [...getters.getFiles],
                            envelope: { ...getters.getEnvelope },
                            signers: getters.getSigners.filter(signer => signer.selected)
                                .map((signer, index) => {
                                    signer.group = getters.getEnvelope?.send_simultaneous ? 1 : index + 1

                                    return signer
                                })
                        }
                        response = await digitalSignature.createV2('envelope', params)

                    } else {

                        const params = {
                            flow: getters.getEnvelope.name,
                            integration_id: getters.getIntegration?.id,
                            file_id: [...getters.getFiles],
                            deal_id: getters.getDeal?.id,
                            signers: getters.getSigners.filter(signer => signer.selected)
                                .map((signer, index) => {
                                    signer.group = index + 1
                                    signer.fields = {
                                        type: getters.getEnvelope.type,
                                        height: 50,
                                        width: 200,
                                        page: 1
                                    }
                                    signer.signatureType = signer.signature_type

                                    return signer
                                })
                        }
                        response = await digitalSignature.createV2('template', params)
                    }
                    Vue.prototype.$toastr.s(response.data.msg)
                    resolve()

                } catch (error) {
					toastFormatMessages(error)
					reject()
                } finally {
                    commit('SET_LOADING', false)
                }
            })
        },
        handleSigners({dispatch, getters}, signers){
            const newSigners = [...signers];
			const newSignersProponent = []

            for (let signer of newSigners) {

                if (getters.getIntegration?.slug === 'docu_sign') {
                    if(signer?.optional == null){
                        signer.optional = false;
                    }
                }

                if(signer.type == 'customer') {
                    let customer = getters.getDeal?.customer

                    // -------------------------------- Cliente -------------------------------- //

                    signer.users_id = customer.id_customer
                    signer.whatsapp_number = customer.mobile
                    signer.selected = true

                    // -------------------------------- Proponentes -------------------------------- //
                    let count = 1
                    // Filtra os grupos de campos configurados como proponentes
                    let groups = (getters.getDeal?.groups || []).filter(group => {
                        return group.fields.map(field => {
                            let has = false

                            // Verifica se alguma das opções do campo, possui uma chave para assinatura
                            if (field.options) {
                                has = !!Object.keys(field.options).find(key => String(key).includes('signature_'))
                            }

                            return has
                        }).find(field => field)
                    })
                    for (const group of groups) {
                        let proponent = proponentCredentials(group.fields)
                        // Verifica se o proponente possui os dados necessários para assinar (Nome e E-mail)
                        if (proponent?.can_sign) {
                            proponent.has_documentation = signer.has_documentation
                            proponent.signature_type = signer.signature_type
                            proponent.type = 'proponent'
                            proponent.role = signer.role
                            proponent.selected = true

                            /**
                             * Para os assinantes do DocuSign, envia-se um objeto com os marcadores
                             * a serem utilizados no documento
                             */
                            if (getters.getIntegration?.slug === 'docu_sign') {
                                const keywordBase = `bidder_${count}`
                                proponent.keyword = $mx_generateSignerKeyword(keywordBase, signer.role)
                            }

                            count += 1
							newSignersProponent.push(proponent)
                       }
                    }
                }
                if(signer.type == 'manager' || signer.type ==  'broker' || signer.type ==  'supervisor'){
                    let user = null
                    //Verifica se o negócio possui um gerente, corretor ou supervisor

                    if(getters.getDeal?.owners[signer.type]?.user) {
                        user = getters.getDeal.owners[signer.type].user
                        signer.name = user?.first_name
                        signer.email = user?.email
                        signer.users_id = user?.id
                        signer.cpf = user?.cpf
                        signer.birthday = user?.birthday
                        signer.whatsapp_number = user?.mobile
                        signer.selected = true
                    }

                }
                if(signer.type == 'proponent' || signer.type == 'nonuser'){
                    signer.selected = true
                }
                //   Seta algumas configurações específicas para o Assine Online
                if (getters.getIntegration?.slug === 'assine_online') {
                    signer.fields = {
                        type: getters.getEnvelope.type,
                        height: 50,
                        width: 200,
                        page: 1
                    }
                }
            }

			return [...newSigners, ...newSignersProponent]
        },
        handleGroups({getters}, signer, count){
            let groups = (getters.getDeal?.groups || []).filter(group => {
                return group.fields.map(field => {
                    let has = false

                    // Verifica se alguma das opções do campo, possui uma chave para assinatura
                    if (field.options) {
                        has = !!Object.keys(field.options).find(key => String(key).includes('signature_'))
                    }

                    return has
                }).find(field => field)
            })
            for (const group of groups) {
                let proponent = proponentCredentials(group.fields)
                // Verifica se o proponente possui os dados necessários para assinar (Nome e E-mail)
                if (proponent?.can_sign) {
                    proponent.has_documentation = signer.has_documentation
                    proponent.signature_type = signer.signature_type
                    proponent.type = 'proponent'
                    proponent.role = signer.role
                    proponent.selected = true

                    /**
                     * Para os assinantes do DocuSign, envia-se um objeto com os marcadores
                     * a serem utilizados no documento
                     */
                    if (getters.getIntegration?.slug === 'docu_sign') {
                        const keywordBase = `bidder_${count}`
                        proponent.keyword = $mx_generateSignerKeyword(keywordBase, signer.role)
                    }

                    signers.splice(signer.group - 1 + count, 0, proponent)
                    count += 1
               }
            }
            return count;
        }
    }
}